/*
 * Copyright (c) 2026 Ambits Technologies LLC. All rights reserved.
 *
 * This source code is the proprietary and confidential property of
 * Ambits Technologies LLC. Unauthorized copying, modification, distribution,
 * or use of this software, via any medium, is strictly prohibited.
 *
 * MyAmbits - AI-Powered Location Safety & Social Platform
 * https://myambits.com
 */

// Thin client for the /api/directions proxy. Called ONCE at Walk Me Home start
// (and only on a major deviation) to fetch the road route polyline + a
// road-accurate ETA — never per location push. Fails soft: any error resolves
// to null so the caller falls back to the straight-line estimate + two-pin map.

import { apiUrl } from "@/utils/apiUrl";
import type { TravelMode } from "@/utils/walkMeHome/travelMode";

export interface DirectionsResult {
  /** Encoded overview polyline (Google algorithm); decode with decodePolyline. */
  polyline: string;
  distanceMeters: number | null;
  durationSeconds: number | null;
}

export async function fetchWmhRoute(input: {
  originLat: number;
  originLng: number;
  destLat: number;
  destLng: number;
  mode: TravelMode;
}): Promise<DirectionsResult | null> {
  try {
    const params = new URLSearchParams({
      originLat: String(input.originLat),
      originLng: String(input.originLng),
      destLat: String(input.destLat),
      destLng: String(input.destLng),
      mode: input.mode,
    });
    const res = await fetch(apiUrl(`/api/directions?${params.toString()}`));
    if (!res.ok) return null;
    const data = (await res.json()) as {
      status?: string;
      route?: {
        polyline?: string;
        distanceMeters?: number | null;
        durationSeconds?: number | null;
      } | null;
    };
    if (!data?.route || !data.route.polyline) return null;
    return {
      polyline: data.route.polyline,
      distanceMeters: data.route.distanceMeters ?? null,
      durationSeconds: data.route.durationSeconds ?? null,
    };
  } catch (err) {
    console.warn("[directionsClient] fetchWmhRoute failed:", err);
    return null;
  }
}
